import { Badge, Button, EmptyState, IconButton, Surface, Table, Td, Th, Tr } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ArticleBarcode, ArticleDetail, ArticleUnit } from '@warehouse/shared';
import { Barcode, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { usePermission } from '../../../auth/auth-store';
import { useConfirm } from '../../../components/ConfirmDialog';
import { FullPageSpinner } from '../../../components/FullPageSpinner';
import { PageHeader } from '../../../components/PageHeader';
import { formatDateTime, formatQty } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import {
  getArticle,
  getArticleHistory,
  removeArticleUnit,
  removeBarcode,
  updateArticle,
  upsertArticleUnit,
} from '../api';
import { BcBadge } from '../components/BcBadge';
import { EntityHistory } from '../components/EntityHistory';
import { LoadFailure } from '../components/LoadFailure';
import { CheckboxField, ReadOnlyField, SelectField, TextField } from '../components/form';
import { Tabs, useTabState } from '../components/Tabs';
import { useUnits } from '../lookups';
import { ArticleForm, toFormValues, toPayload, type ArticleFormValues } from './ArticleForm';
import { BarcodeFormModal } from './BarcodeFormModal';

/**
 * Article detail — Detaljer / Enheter / Streckkoder / Historik.
 *
 * The four tabs are exactly the four things an operator asks about an article:
 * what it is, how its packs convert, which codes resolve to it, and who
 * changed what. All four update live: an `articles` or `barcodes` event
 * invalidates the query rather than patching a row, so the page can never
 * drift from the database.
 */
export function ArticleDetailPage(): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const { id = '' } = useParams();
  const [tab, setTab] = useTabState('details');

  const queryKey = ['master-data', 'article', id];
  const {
    data: article,
    isLoading,
    error,
  } = useQuery({ queryKey, queryFn: () => getArticle(id), enabled: Boolean(id) });
  useLiveQuery(['articles', 'barcodes'], queryKey);

  if (error) return <LoadFailure error={error} />;
  if (isLoading || !article) return <FullPageSpinner />;

  const tabs = [
    { id: 'details', label: t('masterData:articles.tabs.details') },
    { id: 'units', label: t('masterData:articles.tabs.units'), badge: article.units.length },
    { id: 'barcodes', label: t('masterData:articles.tabs.barcodes'), badge: article.barcodes.length },
    { id: 'history', label: t('masterData:articles.tabs.history') },
  ];

  return (
    <>
      <PageHeader
        breadcrumb={<Link to="/registers/articles">{t('masterData:articles.title')}</Link>}
        title={article.name}
        subtitle={article.nameZh ?? undefined}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral">{article.articleNo}</Badge>
            <BcBadge bc={article.bc} />
            {article.orderable ? <Badge tone="success" dot>{t('masterData:articles.fields.orderable')}</Badge> : null}
            {!article.isActive ? <Badge tone="warning">{t('common:common.inactive')}</Badge> : null}
          </div>
        }
      />

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'details' ? <DetailsTab article={article} /> : null}
      {tab === 'units' ? <UnitsTab article={article} /> : null}
      {tab === 'barcodes' ? <BarcodesTab article={article} /> : null}
      {tab === 'history' ? (
        <EntityHistory
          queryKey={['master-data', 'article', id, 'history']}
          fetch={() => getArticleHistory(id)}
          topics={['articles', 'barcodes']}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------

function DetailsTab({ article }: { article: ArticleDetail }): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const queryClient = useQueryClient();
  const canManage = usePermission('registers.articles.manage');

  const mutation = useMutation({
    mutationFn: (values: ArticleFormValues) => updateArticle(article.id, toPayload(values)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-data', 'article', article.id] });
      void queryClient.invalidateQueries({ queryKey: ['master-data', 'articles'] });
      toastSuccess(t('masterData:articles.saved'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  if (!canManage) {
    return (
      <Surface className="grid gap-5 p-6 sm:grid-cols-2 lg:grid-cols-3">
        <ReadOnlyField label={t('masterData:articles.fields.articleNo')} value={article.articleNo} />
        <ReadOnlyField label={t('masterData:articles.fields.name')} value={article.name} />
        <ReadOnlyField label={t('masterData:articles.fields.nameZh')} value={article.nameZh} />
        <ReadOnlyField label={t('masterData:articles.fields.category')} value={article.categoryName} />
        <ReadOnlyField label={t('masterData:articles.fields.baseUnit')} value={article.baseUnitCode} />
        <ReadOnlyField label={t('masterData:articles.fields.supplier')} value={article.supplierName} />
        <ReadOnlyField label={t('masterData:articles.fields.priority')} value={article.priority} />
        <ReadOnlyField
          label={t('masterData:articles.fields.orderable')}
          value={article.orderable ? t('common:common.yes') : t('common:common.no')}
        />
        <ReadOnlyField
          label={t('masterData:articles.fields.lotTracked')}
          value={article.lotTracked ? t('common:common.yes') : t('common:common.no')}
        />
        <ReadOnlyField label={t('masterData:articles.fields.reorderPoint')} value={formatQty(article.reorderPointBase)} />
        <ReadOnlyField label={t('masterData:articles.fields.leadTimeDays')} value={article.leadTimeDays} />
        <ReadOnlyField label={t('common:common.updatedAt')} value={formatDateTime(article.updatedAt)} />
      </Surface>
    );
  }

  return (
    <Surface className="p-6">
      <ArticleForm
        defaultValues={toFormValues(article)}
        bc={article.bc}
        submitting={mutation.isPending}
        submitLabel={t('common:actions.save')}
        onSubmit={(values) => {
          mutation.mutate(values);
        }}
      />
    </Surface>
  );
}

// ---------------------------------------------------------------------------

interface UnitFormValues {
  unitId: string;
  qtyInBase: string;
  isDefaultSales: boolean;
  isDefaultPurchase: boolean;
}

function UnitsTab({ article }: { article: ArticleDetail }): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const queryClient = useQueryClient();
  const canEdit = usePermission('registers.articles.units');
  const { options: unitOptions } = useUnits();
  const { confirm, dialog } = useConfirm();

  const { register, handleSubmit, reset } = useForm<UnitFormValues>({
    defaultValues: { unitId: '', qtyInBase: '', isDefaultSales: false, isDefaultPurchase: false },
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['master-data', 'article', article.id] });
  };

  const upsert = useMutation({
    mutationFn: (values: UnitFormValues) =>
      upsertArticleUnit(article.id, {
        unitId: values.unitId,
        qtyInBase: values.qtyInBase.trim() || '1',
        isDefaultSales: values.isDefaultSales,
        isDefaultPurchase: values.isDefaultPurchase,
      }),
    onSuccess: () => {
      invalidate();
      reset();
      toastSuccess(t('masterData:articleUnits.saved'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const remove = useMutation({
    mutationFn: (articleUnitId: string) => removeArticleUnit(article.id, articleUnitId),
    onSuccess: () => {
      invalidate();
      toastSuccess(t('masterData:articleUnits.removed'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <>
      {dialog}
      <div className="flex flex-col gap-5">
        <p className="text-sm text-ink-muted">{t('masterData:articleUnits.subtitle')}</p>

        <Surface className="overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <Table>
              <thead>
                <tr className="border-b border-surface-border">
                  <Th>{t('masterData:articleUnits.columns.unit')}</Th>
                  <Th className="text-right">{t('masterData:articleUnits.columns.qtyInBase')}</Th>
                  <Th>{t('masterData:articleUnits.columns.defaultSales')}</Th>
                  <Th>{t('masterData:articleUnits.columns.defaultPurchase')}</Th>
                  <Th className="w-16" />
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {article.units.map((unit: ArticleUnit) => (
                  <Tr key={unit.id}>
                    <Td>
                      <span className="font-mono text-sm">{unit.unitCode}</span>
                      <span className="ml-2 text-sm text-ink-muted">{unit.unitName}</span>
                      {unit.isBaseUnit ? (
                        <Badge tone="brand" size="sm" className="ml-2">
                          {t('masterData:articleUnits.baseUnit')}
                        </Badge>
                      ) : null}
                    </Td>
                    <Td className="text-right font-mono">{formatQty(unit.qtyInBase)}</Td>
                    <Td>{unit.isDefaultSales ? <Badge tone="success" size="sm">{t('common:common.yes')}</Badge> : '—'}</Td>
                    <Td>
                      {unit.isDefaultPurchase ? <Badge tone="success" size="sm">{t('common:common.yes')}</Badge> : '—'}
                    </Td>
                    <Td>
                      {canEdit && !unit.isBaseUnit ? (
                        <IconButton
                          variant="danger"
                          aria-label={t('common:actions.delete')}
                          onClick={() => {
                            void (async () => {
                              const ok = await confirm({
                                title: t('masterData:articleUnits.confirmRemove.title'),
                                message: t('masterData:articleUnits.confirmRemove.message', {
                                  unit: unit.unitCode,
                                }),
                              });
                              if (ok) remove.mutate(unit.id);
                            })();
                          }}
                        >
                          <Trash2 className="size-4" />
                        </IconButton>
                      ) : null}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Surface>

        {canEdit ? (
          <Surface className="p-5">
            <h3 className="mb-4 font-mono text-xs uppercase tracking-[0.14em] text-ink-muted">
              {t('masterData:articleUnits.add')}
            </h3>
            <form
              onSubmit={(event) => {
                void handleSubmit((values) => {
                  upsert.mutate(values);
                })(event);
              }}
              className="grid items-end gap-4 sm:grid-cols-2 lg:grid-cols-4"
              noValidate
            >
              <SelectField
                label={t('masterData:articleUnits.fields.unit')}
                options={[{ value: '', label: '—' }, ...unitOptions]}
                {...register('unitId', { required: true })}
              />
              <TextField
                label={t('masterData:articleUnits.fields.qtyInBase')}
                inputMode="decimal"
                hint={t('masterData:articleUnits.hints.qtyInBase')}
                {...register('qtyInBase')}
              />
              <CheckboxField
                label={t('masterData:articleUnits.fields.isDefaultSales')}
                {...register('isDefaultSales')}
              />
              <div className="flex items-center gap-4">
                <CheckboxField
                  label={t('masterData:articleUnits.fields.isDefaultPurchase')}
                  {...register('isDefaultPurchase')}
                />
                <Button type="submit" size="sm" icon={<Plus />} loading={upsert.isPending}>
                  {t('common:actions.add')}
                </Button>
              </div>
            </form>
          </Surface>
        ) : null}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------

function BarcodesTab({ article }: { article: ArticleDetail }): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const queryClient = useQueryClient();
  const canManage = usePermission('registers.barcodes.manage');
  const { confirm, dialog } = useConfirm();
  const [adding, setAdding] = useState(false);

  const remove = useMutation({
    mutationFn: (id: string) => removeBarcode(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-data', 'article', article.id] });
      void queryClient.invalidateQueries({ queryKey: ['master-data', 'barcodes'] });
      toastSuccess(t('masterData:barcodes.removed'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <>
      {dialog}
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-3xl text-sm text-ink-muted">{t('masterData:barcodes.subtitle')}</p>
          {canManage ? (
            <Button
              icon={<Plus />}
              size="sm"
              onClick={() => {
                setAdding(true);
              }}
            >
              {t('masterData:barcodes.new')}
            </Button>
          ) : null}
        </div>

        {article.barcodes.length === 0 ? (
          <EmptyState
            icon={<Barcode />}
            title={t('masterData:barcodes.empty')}
            description={t('masterData:barcodes.emptyDescription')}
          />
        ) : (
          <Surface className="overflow-hidden">
            <div className="overflow-x-auto scrollbar-thin">
              <Table>
                <thead>
                  <tr className="border-b border-surface-border">
                    <Th>{t('masterData:barcodes.columns.barcode')}</Th>
                    <Th>{t('masterData:barcodes.columns.kind')}</Th>
                    <Th>{t('masterData:barcodes.columns.supplier')}</Th>
                    <Th>{t('masterData:barcodes.columns.unit')}</Th>
                    <Th className="text-right">{t('masterData:barcodes.columns.qtyPerScan')}</Th>
                    <Th>{t('masterData:barcodes.columns.taughtBy')}</Th>
                    <Th className="w-16" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {article.barcodes.map((barcode: ArticleBarcode) => (
                    <Tr key={barcode.id}>
                      <Td className="font-mono text-sm">{barcode.barcode}</Td>
                      <Td>
                        <Badge tone={barcode.kind === 'gtin' ? 'brand' : 'neutral'} size="sm">
                          {t(`masterData:barcodes.kinds.${barcode.kind}`)}
                        </Badge>
                      </Td>
                      <Td>{barcode.supplierName ?? '—'}</Td>
                      <Td className="font-mono text-xs">{barcode.unitCode ?? '—'}</Td>
                      <Td className="text-right font-mono">{formatQty(barcode.qtyPerScan)}</Td>
                      <Td className="text-sm text-ink-muted">
                        {barcode.taughtByName ?? t('masterData:barcodes.manual')}
                      </Td>
                      <Td>
                        {canManage ? (
                          <IconButton
                            variant="danger"
                            aria-label={t('common:actions.delete')}
                            onClick={() => {
                              void (async () => {
                                const ok = await confirm({
                                  title: t('masterData:barcodes.confirmRemove.title'),
                                  message: t('masterData:barcodes.confirmRemove.message', {
                                    barcode: barcode.barcode,
                                  }),
                                });
                                if (ok) remove.mutate(barcode.id);
                              })();
                            }}
                          >
                            <Trash2 className="size-4" />
                          </IconButton>
                        ) : null}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Surface>
        )}
      </div>

      {adding ? (
        <BarcodeFormModal
          articleId={article.id}
          fixedArticle={`${article.articleNo} — ${article.name}`}
          defaultSupplierId={article.supplierId}
          onClose={() => {
            setAdding(false);
          }}
        />
      ) : null}
    </>
  );
}
